<script lang="ts">
    import { redirect } from "@roxi/routify";
    import { AppStore } from "$/stores/AppStore";
    import { ClusterStore, ClusterAccessLevel } from "$/stores/ClusterStore";
    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { getEffectivePricingPlan } from "$/utils/Utils";

    import PanelDeleteSection from "../_components/PanelDeleteSection.svelte";
    import Panel from "../_components/Panel.svelte";
    import PanelHeader from "../_components/PanelHeader.svelte";

    import TopicRole from "$/components/TopicRole.svelte";
    import AddTopicRole from "$/components/AddTopicRole.svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import SvgIcon from "$/components/SVGIcon.svelte";
    import InviteNewUsersPlaceholder from "$/components/InviteNewUsersPlaceholder.svelte";

    $: clusterOwner =
        $ClusterStore.active?.clusterRoles?.length > 0
            ? $ClusterStore.active.clusterRoles.find((role) => role.role === 10)
                  .user
            : null;

    $: effectivePricingPlan = getEffectivePricingPlan($TenantStore?.active);

    $: clustersAccessLevel = $ClusterStore.active?.accessLevel;

    $: $ClusterStore.active, loadPrivatizePermission();

    let hasPrivatizeClusterPemission = false;

    function loadPrivatizePermission() {
        hasPrivatizeClusterPemission =
            !TenantStore.isPersonal() &&
            ClusterStore.isOwner($TokenStore?.user?.id);
    }

    function handleClusterNameChanged(event) {
        $ClusterStore.active.name =
            event.target.value.trim() === ""
                ? "Untitled Cluster"
                : event.target.value.trim();
        ClusterStore.updateCluster({
            id: $ClusterStore.active.id,
            name: $ClusterStore.active.name,
        });
        event.target.value = $ClusterStore.active.name;
    }

    function clustersAccessLevelChanged(event) {
        clustersAccessLevel = Number(event.target.value);
        $ClusterStore.active.accessLevel = clustersAccessLevel;
        ClusterStore.updateCluster({
            id: $ClusterStore.active.id,
            accessLevel: clustersAccessLevel,
        });
    }

    async function handleClusterDeleted() {
        await ClusterStore.deleteCluster($ClusterStore.active);
        AppStore.closePanels();
        if (
            $ActiveTopic &&
            $ClusterStore.active.id === $ActiveTopic.clusterId
        ) {
            ActiveTopicStore.clear();
            $redirect("/");
        }
    }

    async function handleRoleChanged(roleId, roleValue) {
        let role = {
            id: roleId,
            role: roleValue,
        };

        ClusterStore.updateClusterRole(role, $ClusterStore.active.id);
    }

    async function handleRoleDeleted(roleId) {
        let deletedRole = $ClusterStore.active.clusterRoles.find(
            (role) => role.id === roleId,
        );
        await ClusterStore.deleteClusterRole(roleId, $ClusterStore.active.id);
        TenantStore.refreshCollaboratorsList();
        if ($ActiveTopic && deletedRole) {
            removeCollaboratorFromActiveTopic(deletedRole.user);
        }
    }

    async function handleRoleAdded(role) {
        let newRole = await ClusterStore.createClusterRole(
            role,
            $ClusterStore.active.id,
        );
        TenantStore.addCollaborator(newRole.user);
        if ($ActiveTopic) {
            addCollaboratorToActiveTopic(newRole.user);
        }
    }

    function addCollaboratorToActiveTopic(user) {
        let activeTopicInCluster = $ClusterStore.active.topics.find(
            (clusterTopic) => clusterTopic.id === $ActiveTopic.id,
        );
        if (activeTopicInCluster !== undefined) {
            ActiveTopicStore.addCollaborator(user);
        }
    }

    function removeCollaboratorFromActiveTopic(user) {
        let activeTopicInCluster = $ClusterStore.active.topics.find(
            (clusterTopic) => clusterTopic.id === $ActiveTopic.id,
        );
        if (activeTopicInCluster !== undefined) {
            let roleInActiveTopic = $ActiveTopic.roles.find(
                (role) => role.user.id === user.id,
            );
            if (roleInActiveTopic === undefined) {
                ActiveTopicStore.removeCollaborator(user);
            }
        }
    }
</script>

<Panel>
    <PanelHeader title="Cluster Settings" on:close={AppStore.closePanels} />

    <div class="panel-content">
        <div class="panel-group">
            <h2 class="panel-group__header">Cluster name</h2>
            <input
                class="cluster-name-input"
                type="text"
                placeholder="Cluster name"
                on:change={handleClusterNameChanged}
                disabled={effectivePricingPlan === -1}
                value={$ClusterStore.active?.name}
            />
        </div>

        <div class="panel-group">
            <h2 class="panel-group__header">Cluster members</h2>

            {#if $ClusterStore.active}
                {#if $ClusterStore.active?.clusterRoles?.length > 0}
                    {#each $ClusterStore.active?.clusterRoles as role, index(role.id)}
                            <TopicRole
                                {role}
                                editable={role.role !== 10}
                                on:edit={(event) =>
                                    handleRoleChanged(
                                        role.id,
                                        event.detail.role,
                                    )}
                                on:delete={() => handleRoleDeleted(role.id)}
                            />
                    {/each}
                {/if}

                {#if effectivePricingPlan === -1}
                    <InviteNewUsersPlaceholder
                        planName="Pro"
                        ctaClick={() => $redirect("/payment")}
                    />
                {:else}
                    <AddTopicRole
                        owner={clusterOwner}
                        existingRoles={$ClusterStore.active?.clusterRoles}
                        addRole={handleRoleAdded}
                    />
                {/if}
            {:else}
                <div class="panel-content__message">
                    Loading cluster members...
                </div>
            {/if}
        </div>
        {#if hasPrivatizeClusterPemission}
            <div class="public-role">
                <h2 class="public-role-header">
                    General Access

                    <a
                        href="https://help.collabwriting.com/en/articles/8030890-how-to-use-cluster-access-roles"
                        target="_blank"
                        rel="noreferrer"
                        class="public-role-header__question"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                            />
                        </svg>
                    </a>
                </h2>
                <div class="public-role-settings">
                    <div class="public-role-settings__icon">
                        {#if clustersAccessLevel === ClusterAccessLevel.WORKSPACE}
                            <SvgIcon name="squares-2x2" />
                        {:else}
                            <SvgIcon name="lock" />
                        {/if}
                    </div>

                    <div class="public-role-settings__general">
                        <select
                            class="public-role-settings__general-select"
                            value={clustersAccessLevel}
                            on:change={clustersAccessLevelChanged}
                        >
                            <option value={ClusterAccessLevel.WORKSPACE}
                                >Workspace</option
                            >
                            <option value={ClusterAccessLevel.RESTRICTED}
                                >Restricted</option
                            >
                        </select>
                        <div class="public-role-settings__general-info">
                            {#if clustersAccessLevel === ClusterAccessLevel.WORKSPACE}
                                All workspace members see this cluster.
                            {:else if clustersAccessLevel === ClusterAccessLevel.RESTRICTED}
                                Only cluster members see this cluster.
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
        {/if}

        {#if effectivePricingPlan !== -1}
            <PanelDeleteSection
                deletionConfirmed={handleClusterDeleted}
                description={"Deleting a cluster is permanent and cannot be undone."}
                warningText={"Are you <b>absolutely sure</b> you want to delete this cluster?"}
            />
        {/if}
    </div>
</Panel>

<style>
    @import "../../../main.css";

    .panel-content {
        @apply reset flex flex-col gap-4 overflow-auto px-4 py-4 !important;
    }
    .panel-group {
        @apply reset flex flex-shrink-0 flex-col gap-2 !important;
    }
    .panel-group__header {
        @apply reset !important;
    }
    .panel-content__message {
        @apply reset text-sm text-gray-400 !important;
    }
    .cluster-name-input {
        @apply flex w-full flex-row items-center gap-1 rounded-full border border-gray-200 bg-white p-4 text-sm outline-none !important;
    }

    .public-role {
        @apply flex flex-col !important;
    }
    .public-role-header {
        @apply reset mb-2 flex flex-row items-center justify-between !important;
    }
    .public-role-header__question {
        @apply reset !important;
    }
    .public-role-header__question svg {
        @apply reset h-4 w-4 text-gray-400 hover:text-black !important;
    }
    .public-role-settings {
        @apply flex flex-row items-center gap-[0.3em] rounded-2xl border border-gray-200 bg-white px-4 py-2 !important;
    }
    .public-role-settings__icon svg {
        @apply h-5 w-5 !important;
    }

    .public-role-settings__general {
        @apply flex w-full flex-col !important;
    }
    .public-role-settings__general-select {
        @apply reset appearance-auto text-sm !important;
    }
    .public-role-settings__general-select:disabled {
        @apply reset appearance-none pl-1 !important;
    }
    .public-role-settings__general-info {
        @apply reset pl-1 text-xs text-gray-500 !important;
    }

    .public-role-settings__public-select {
        @apply reset ml-auto appearance-auto bg-white text-right text-xs !important;
    }
    .public-role-settings__public-select:disabled {
        @apply appearance-none bg-white italic !important;
    }
</style>
